import { actions } from '..';
import * as api from '../../api';
import { addTaskItem, deleteTask, deleteTaskItem, getNewTask, updateTaskItem, updateTaskState, updateTaskSubject } from '../../api/task-control';
import { untrackedMediaDescription } from '../../Components/AgentView/TasksPage';
import { CallType, ClidObjectType, EMPTY_GUID, Media, TaskItemAction } from '../../model';
import { selectMyRegion, selectMyself } from '../reducers/config';
import { selectEmail } from '../reducers/emails';
import { selectMedia, selectMediaByContactId, selectMediaByTaskId } from '../reducers/media';
import { selectQueue } from '../reducers/queues';
import { selectItemByItemId, selectTaskByItemId } from '../reducers/tasks';
import { all, SagaContext } from 'async-saga';
import { takeEvery } from '../tools/async-saga-types';

const fetchMediaTasksRequest = takeEvery(actions.fetchMediaTasksRequest, async function (ctx, { mediaId, contactId }) {
  const myself = selectMyself(ctx.getState())
  if (myself) {
    const tasks = await api.getMediaTasks(myself, contactId)
    ctx.dispatch(actions.fetchMediaTasksResponse(contactId === '' ? mediaId : contactId, tasks))
  }
})

const fetchMediaTaskItemsRequest = takeEvery(actions.fetchMediaTaskItemsRequest, async function (ctx, { mediaId, taskId }) {
  const myself = selectMyself(ctx.getState())
  const media = selectMedia(ctx.getState(), mediaId)
  if (!myself || !media) { return }

  if (media.clidObjectKey !== '' && (media.clidObjectType === ClidObjectType.companyContact || media.clidObjectType === ClidObjectType.personalContact || media.clidObjectType === ClidObjectType.customerContact)) {
    if (taskId === EMPTY_GUID) {
      const itemId = getMediaTaskItemId(ctx, media);
      const items = await api.getMediaTaskItemsUnclassifed(myself, media.clidObjectKey, itemId)
      ctx.dispatch(actions.fetchMediaTaskItemsResponse(mediaId, media.clidObjectKey, taskId, items))
    } else {
      const items = await api.getMediaTaskItems(myself, taskId)
      ctx.dispatch(actions.fetchMediaTaskItemsResponse(mediaId, media.clidObjectKey, taskId, items))
    }
  } else if (media.clidObjectKey === '') {
    let value = media.clid
    switch (media.mediaType) {
      case 'EMAIL':

        let email = selectEmail(ctx.getState(), mediaId)
        if (!email) {
          selectEmail(ctx.getState(), media.mediaItemId)
        }
        if (email) {
          value = email.original.from.address
        }

        break;
      case 'TC':
        value = media.mediaItemId

        break;
      case 'FB':
        value = media.clidDescription
    }

    const items = await api.getMediaItemsUnclassifed(myself, media.mediaType, value)
    ctx.dispatch(actions.fetchMediaTaskItemsResponse(mediaId, '', taskId, items))
  }
})

const createNewMediaTask = takeEvery(actions.createNewMediaTask, async function (ctx, { mediaId, mediaType, contactId }) {
  const myself = selectMyself(ctx.getState())
  const media = selectMedia(ctx.getState(), mediaId)
  const region = selectMyRegion(ctx.getState())
  const queue = media && selectQueue(ctx.getState(), { id: media.queueId, server: media.server })
  if (myself && media && region) {

    const itemId = getMediaTaskItemId(ctx, media)
    const subject = untrackedMediaDescription(media, region, queue)

    if (subject === '') {
      if (media.mediaType === 'EMAIL') {
        ctx.dispatch(actions.showClientNotification('Please enter a subject for the email'))
      }

      return
    }

    if (itemId) {
      let action = TaskItemAction.none
      switch (mediaType) {
        case "TC":
          action = TaskItemAction.none
          break;
        case "EMAIL":
          if (media.callType === CallType.Queue) {
            action = TaskItemAction.incoming
          } else if (media.callType === CallType.Outgoing) {
            action = TaskItemAction.outgoing
          } else {
            action = TaskItemAction.outgoing
          }
          break;
        case "CALL":
          if (media.callType === CallType.Queue) {
            action = TaskItemAction.queue
          } else if (media.callType === CallType.Incoming) {
            action = TaskItemAction.incoming
          } else if (media.callType === CallType.Outgoing) {
            action = TaskItemAction.outgoing
          }
          break;
        case "TWEET":
          if (media.callType === CallType.Queue) {
            action = TaskItemAction.incoming
          } else {
            action = TaskItemAction.outgoing
          }
          break;
        case "FB":
          if (media.callType === CallType.Queue) {
            action = TaskItemAction.incoming
          } else {
            action = TaskItemAction.outgoing
          }
          break;
        case "OUT":
          if (media.callType === CallType.Queue) {
            action = TaskItemAction.incoming
          } else {
            action = TaskItemAction.outgoing
          }
          break;
      }

      const task = await getNewTask(myself, itemId, contactId, mediaType, mediaId, subject, action)

      ctx.dispatch(actions.createNewTaskResponse(contactId, task))
      ctx.dispatch(actions.fetchMediaTaskItemsRequest(mediaId, task.taskId))
      ctx.dispatch(actions.fetchMediaTaskItemsRequest(mediaId, EMPTY_GUID))
    }
  }
})

const deleteMediaTask = takeEvery(actions.deleteMediaTaskRequest, async function (ctx, { contactId, taskId }) {
  const myself = selectMyself(ctx.getState())
  if (myself) {
    await deleteTask(myself, taskId)

    //reload media since we have deleted an item
    const active = selectMediaByContactId(ctx.getState(), contactId)
    active.forEach(m => {
      ctx.dispatch(actions.fetchMediaTaskItemsRequest(m.id, EMPTY_GUID))
    });

    ctx.dispatch(actions.deleteMediaTaskAction(contactId, taskId))
  }
})

const updateMediaTaskSubject = takeEvery(actions.updateMediaTaskSubjectRequest, async function (ctx, { taskId, subject }) {
  const myself = selectMyself(ctx.getState())
  if (myself) {
    await updateTaskSubject(myself, taskId, subject)
  }
})

const updateMediaTaskStateRequest = takeEvery(actions.updateMediaTaskStateRequest, async function (ctx, { taskId, stateId }) {
  const myself = selectMyself(ctx.getState())
  if (myself) {
    await updateTaskState(myself, taskId, stateId)
  }
})


const deleteMediaItemTask = takeEvery(actions.deleteMediaItemTask, async function (ctx, { itemId }) {
  const myself = selectMyself(ctx.getState())
  const task = selectTaskByItemId(ctx.getState(), itemId)
  const item = selectItemByItemId(ctx.getState(), itemId)

  if (myself && task && item) {
    const media = selectMediaByTaskId(ctx.getState(), task.taskId)

    //delete item and clean hack by passing media id that has a matched task id
    await deleteTaskItem(myself, task.taskId, item.itemId, item.mediaTypeId, media !== null ? media.id : '', item.action)

    //reload media since we have deleted an item
    const active = selectMediaByContactId(ctx.getState(), task.contactId.toString())
    active.forEach(m => {
      ctx.dispatch(actions.fetchMediaTaskItemsRequest(m.id, task.taskId))
    });
  }
})

function getMediaTaskItemAction(media: Media) {
  switch (media.mediaType) {
    case 'VM':
    case 'CALL':
      {
        if (media.isQueueCall) {
          return TaskItemAction.queue
        } else if (media.callType !== CallType.Incoming) {
          return TaskItemAction.incoming
        } else {
          return TaskItemAction.incoming
        }
      }
    case 'EMAIL':
      {
        return TaskItemAction.incoming
      }
    case 'TC':
      {
        return TaskItemAction.incoming
      }
  }
  return TaskItemAction.none
}

function getMediaTaskItemId(ctx: SagaContext<any>, media: Media) {
  switch (media.mediaType) {
    case 'VM':
    case 'CALL':
      return media.id
    default:
      {
        return media.mediaItemId
      }
  }
}

const moveMediaItemTask = takeEvery(actions.moveMediaItemTask, async function (ctx, { mediaId, taskId, itemId }) {
  const myself = selectMyself(ctx.getState())
  const region = selectMyRegion(ctx.getState())
  const media = selectMedia(ctx.getState(), mediaId)

  if (media && region && myself) {
    //if no itemId then we are adding current call to a task
    if (!itemId) {
      //get item id
      itemId = getMediaTaskItemId(ctx, media)

      if (itemId) {
        const queue = selectQueue(ctx.getState(), { id: media.queueId, server: media.server })

        const item = await addTaskItem(myself, taskId, itemId, media.mediaType, untrackedMediaDescription(media, region, queue), getMediaTaskItemAction(media), mediaId)

        ctx.dispatch(actions.addMediaItemTask(media.clidObjectKey, taskId, item))
      }
    } else {
      const item = selectItemByItemId(ctx.getState(), itemId)
      if (item) {

        const updatedItem = await updateTaskItem(myself, taskId, itemId, item.mediaTypeId, item.subject, item.action, mediaId)

        //find the old item in the data and remove it
        const task = selectTaskByItemId(ctx.getState(), itemId)
        if (task) {
          ctx.dispatch(actions.removeMediaItemTask(media.clidObjectKey, task.taskId, itemId))
        }
        //add the new one
        ctx.dispatch(actions.addMediaItemTask(media.clidObjectKey, taskId, updatedItem))
      }
    }
  }
})

export default all(
  fetchMediaTasksRequest,
  fetchMediaTaskItemsRequest,
  createNewMediaTask,
  deleteMediaTask,
  updateMediaTaskSubject,
  deleteMediaItemTask,
  moveMediaItemTask,
  updateMediaTaskStateRequest,
)
